import {
  Flex,
  Icon,
  Td,
  Text,
  Tr,
  useBreakpointValue,
  Box,
  Checkbox,
  IconButton,
  Tooltip,
  Spinner,
  useToast,
  VStack,
  HStack
} from "@chakra-ui/react";
import React, { useState } from "react";
import { FaUser, FaFileInvoiceDollar, FaDownload, FaFilePdf } from "react-icons/fa";
import { StatusBadge } from "utils/site";
import { formatCurrency } from "services/billing";
import axios from 'axios';
import { getSiteConfig } from 'utils/site';
import { formatChileDate, formatChileTime } from 'utils/dateUtils';

const PaymentTableRow = (props) => {
  const {
    id,
    date_paid,
    date_modified,
    billing,
    customer_info,
    total,
    status,
    meta_data = [],
    line_items = [],
    onClick,
    isSelected = false,
    onSelect,
    showCheckbox = false
  } = props;

  const textColor = "ink.900";
  const isMobile = useBreakpointValue({ base: true, md: false });
  const toast = useToast();
  const [loadingPdf, setLoadingPdf] = useState(false);
  
  // Obtener billmysales_order_id e invoice_number de meta_data
  const billmysalesOrderId = meta_data.find(meta => meta.key === 'billmysales_order_id')?.value;
  const invoiceNumber = meta_data.find(meta => meta.key === 'billmysales_invoice_number')?.value;

  // Extraer glosa del PRIMER LINE ITEM (donde está realmente la glosa)
  let glosa = '';
  let concepto = 'Mensualidad';
  let mes = '';

  if (line_items && line_items.length > 0) {
    const firstItem = line_items[0];
    const itemMeta = firstItem.meta_data || [];

    glosa = itemMeta.find(meta => meta.key === 'glosa')?.value || '';
    concepto = itemMeta.find(meta => meta.key === 'concepto')?.value || firstItem.name || 'Mensualidad';
    mes = itemMeta.find(meta => meta.key === 'mes')?.value || '';
  }

  // Fallback: si no hay line_items, intentar leer de meta_data de orden (compatibilidad con órdenes antiguas)
  if (!glosa && meta_data.length > 0) {
    glosa = meta_data.find(meta => meta.key === 'glosa')?.value || '';
    if (!concepto || concepto === 'Mensualidad') {
      concepto = meta_data.find(meta => meta.key === 'concepto')?.value || 'Mensualidad';
    }
    if (!mes) {
      mes = meta_data.find(meta => meta.key === 'mes')?.value || '';
    }
  }

  const grupo = meta_data.find(meta => meta.key === 'customer_group_name')?.value ||
               meta_data.find(meta => meta.key === 'grupo')?.value || 'Sin grupo';

  // Detectar si es Diciembre Cuota 1 (matrícula)
  const esDiciembreCuota1 = meta_data.find(meta => meta.key === 'cuota')?.value === 'Cuota 1' ||
                            concepto.includes('Matrícula') ||
                            (mes && mes.endsWith('-12-31'));

  // Usar glosa si está disponible, sino fallback a concepto
  const descripcion = glosa || concepto;

  // Usar customer_info si está disponible, sino usar billing
  const customerData = customer_info || billing || {};
  const customerName = customerData.first_name && customerData.last_name 
    ? `${customerData.first_name} ${customerData.last_name}`.trim()
    : customerData.username || 'Sin nombre';
  
  // Extraer datos del apoderado de meta_data
  const apoderadoNombre = meta_data.find(meta => meta.key === 'customer_apoderado_nombre')?.value || '';
  const apoderadoRut = meta_data.find(meta => meta.key === 'customer_apoderado_rut')?.value || '';
  const apoderadoEmail = meta_data.find(meta => meta.key === 'customer_apoderado_email')?.value || '';
  const apoderadoTelefono = meta_data.find(meta => meta.key === 'customer_apoderado_telefono')?.value || '';

  // Determinar medio de pago basado en payment_method_title y meta_data
  const getPaymentMethod = () => {
    // Primero verificar si tiene payment_method_title
    if (props.payment_method_title) {
      // Si contiene "Webpay", es Webpay
      if (props.payment_method_title.toLowerCase().includes('webpay')) {
        return 'Webpay';
      }
      // Si no es Webpay, retornar el título del método tal como está
      return props.payment_method_title;
    }
    
    // Verificar en meta_data si hay información de transacción de Webpay
    const hasWebpayTransaction = meta_data.some(meta => 
      meta.key === 'webpay_transaction_id' || 
      meta.key === 'transactionStatus' ||
      meta.key === 'paymentType'
    );
    
    if (hasWebpayTransaction) {
      return 'Webpay';
    }
    
    // Si no hay indicadores de Webpay, es presencial
    return 'Presencial';
  };

  const paymentMethod = getPaymentMethod();

  // Formatear fechas y horas para Chile usando utilidades
  const formattedDatePaid = formatChileDate(date_paid);
  const formattedTimePaid = formatChileTime(date_paid);
  const formattedDateModified = formatChileDate(date_modified);
  const formattedTimeModified = formatChileTime(date_modified);


  // Formatear mes/año desde meta_data
  const formatMesAno = (mesValue) => {
    if (!mesValue) return '-';
    
    // Formato esperado: "2025-11"
    const [year, month] = mesValue.split('-');
    if (!year || !month) return mesValue;
    
    const mesesES = {
      '01': 'Ene', '02': 'Feb', '03': 'Mar', '04': 'Abr',
      '05': 'May', '06': 'Jun', '07': 'Jul', '08': 'Ago',
      '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dic'
    };
    
    return `${mesesES[month] || month}/${year.slice(2)}`;
  };

  // Pill de estado personalizado para pagos (paleta Datofy V1)
  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      'completed': { bg: 'ok.bg', color: 'ok.500', label: 'Completado' },
      'pending': { bg: 'warn.bg', color: 'warn.500', label: 'Pendiente' },
      'processing': { bg: 'sky.bg', color: 'sky.ink', label: 'Procesando' },
      'on-hold': { bg: 'butter.bg', color: 'butter.ink', label: 'En espera' },
      'cancelled': { bg: 'danger.bg', color: 'danger.500', label: 'Cancelado' },
      'refunded': { bg: 'lav.bg', color: 'lav.ink', label: 'Reembolsado' },
      'failed': { bg: 'danger.bg', color: 'danger.500', label: 'Fallido' },
      'repactado': { bg: 'teal.bg', color: 'teal.ink', label: 'Repactado' }
    };

    const config = statusConfig[status] || { bg: 'cream.bg2', color: 'ink.400', label: status };

    return (
      <Box as="span" px="8px" py="2px" borderRadius="999px" bg={config.bg} color={config.color} fontSize="10.5px" fontWeight="700">
        {config.label}
      </Box>
    );
  };

  // Manejar click en checkbox
  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    // NO usar preventDefault aquí para permitir el comportamiento normal del checkbox
    if (onSelect) {
      onSelect(e.target.checked);
    }
  };
  
  // Manejar click en la celda del checkbox
  const handleCheckboxCellClick = (e) => {
    // Solo detener propagación si NO es click directo en el checkbox
    if (e.target.type !== 'checkbox') {
      e.stopPropagation();
    }
  };
  
  // Función para obtener PDF de BillMySales
  const handleGetBillMySalesPdf = async (e) => {
    e.stopPropagation();
    setLoadingPdf(true);
    try {
      const config = JSON.parse(getSiteConfig());
      const wpApiUrl = config?.configs?.find(c => c.key === 'WP_API_URL')?.value;
      
      if (!wpApiUrl) {
        throw new Error('WP_API_URL no configurado');
      }

      const response = await axios.get(`${wpApiUrl}/datofy/v1/orders/${id}/billmysales-pdf`);
      
      if (response.data.success) {
        // Si hay link PDF, abrirlo automáticamente
        if (response.data.links?.pdf) {
          window.open(response.data.links.pdf, '_blank');
        }
        
        toast({
          title: 'PDF Obtenido',
          description: `Factura #${response.data.invoice_data?.number || 'N/A'} - ${response.data.invoice_data?.document || 'Documento'}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Sin BillMySales ID',
          description: response.data.message || 'Esta orden no tiene BillMySales Order ID asociado',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error obteniendo PDF de BillMySales:', error);
      toast({
        title: 'Error',
        description: 'No se pudo obtener el PDF. Verifique que la orden tenga BillMySales ID.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingPdf(false);
    }
  };

  if (isMobile) {
    return (
      <Tr onClick={onClick} cursor="pointer" borderTop="1px solid" borderColor="cream.line" _hover={{ bg: "coral.tint" }} transition="background 0.13s">
        <Td border="none">
          <Flex direction="column" gap={2}>
            {/* Checkbox y información principal */}
            <Flex align="center" gap={3}>
              {showCheckbox && (
                <Box onClick={handleCheckboxCellClick}>
                  <Checkbox
                    isChecked={isSelected}
                    onChange={handleCheckboxClick}
                    colorScheme="orange"
                  />
                </Box>
              )}
              <Box flex="1">
                <Text fontSize="md" color={textColor} fontWeight="800">
                  {customerName}
                </Text>
              </Box>
            </Flex>

            <Box>
              <Flex align="center" mt={1}>
                <Icon as={FaUser} h="16px" w="16px" mr={2} color="ink.400" />
                <Text fontSize="sm" color="ink.400">
                  {billing.email}
                </Text>
              </Flex>
            </Box>

            {/* Detalles del Pago */}
            <Box>
              <Text fontSize="sm" color="ink.400">
                #{id}
              </Text>
              <Flex align="center" gap={2}>
                <Text fontSize="sm" color="ink.700" noOfLines={2} flex="1">
                  {descripcion}
                </Text>
                {esDiciembreCuota1 && (
                  <Box as="span" px="8px" py="2px" borderRadius="999px" bg="lav.bg" color="lav.ink" fontSize="10.5px" fontWeight="700">
                    MATRÍCULA
                  </Box>
                )}
              </Flex>
              <Text fontSize="sm" color="ink.400">
                Pagado: {formattedDatePaid} {formattedTimePaid}
              </Text>
              {mes && (
                <Text fontSize="sm" color="sky.ink" fontWeight="600">
                  📅 {formatMesAno(mes)}
                </Text>
              )}
              <Text fontSize="md" color={textColor} fontWeight="800">
                {formatCurrency(total)}
              </Text>
              <Text fontSize="xs" color="ink.400">
                💳 {paymentMethod}
              </Text>
              {invoiceNumber && (
                <Text fontSize="xs" color="sky.ink" fontWeight="700">
                  🧾 {invoiceNumber}
                </Text>
              )}
            </Box>

            {/* Estado */}
            <Box>
              {getPaymentStatusBadge(status)}
            </Box>
          </Flex>
        </Td>
      </Tr>
    );
  }

  // Vista Desktop
  return (
    <Tr onClick={onClick} cursor="pointer" borderTop="1px solid" borderColor="cream.line" _hover={{ bg: "coral.tint" }} transition="background 0.13s" height="70px">
      {/* Checkbox */}
      {showCheckbox && (
        <Td border="none" width="40px" py={2} onClick={handleCheckboxCellClick}>
          <Checkbox
            isChecked={isSelected}
            onChange={handleCheckboxClick}
            colorScheme="orange"
          />
        </Td>
      )}

      {/* Columna Estado + ID + Precio */}
      <Td border="none" minWidth={{ sm: "100px" }} py={2}>
        <VStack align="flex-start" spacing={1}>
          {/* ID arriba */}
          <Text fontSize="xs" color="ink.400">
            #{id}
          </Text>

          {/* Estado */}
          {getPaymentStatusBadge(status)}

          {/* Badge de Diciembre Cuota 1 */}
          {esDiciembreCuota1 && (
            <Box as="span" px="6px" py="1px" borderRadius="999px" bg="lav.bg" color="lav.ink" fontSize="9.5px" fontWeight="700">
              MAT
            </Box>
          )}

          {/* Precio */}
          <Text fontSize="xs" color={textColor} fontWeight="800">
            {formatCurrency(total)}
          </Text>
        </VStack>
      </Td>

      {/* Columna Mes */}
      <Td border="none" minWidth={{ sm: "35px" }} maxWidth={{ sm: "35px" }} py={2}>
        {mes ? (
          <Text fontSize="xs" color="sky.ink" fontWeight="700" textAlign="center">
            {formatMesAno(mes)}
          </Text>
        ) : (
          <Text fontSize="xs" color="ink.400" textAlign="center">-</Text>
        )}
      </Td>

      {/* Columna Alumno */}
      <Td border="none" minWidth={{ sm: "160px" }} py={2}>
        <Text fontSize="sm" color={textColor} fontWeight="800" lineHeight="1.2">
          {customerName}
        </Text>
        <Flex align="center" mt={1} minWidth="100%" flexWrap="nowrap">
          <Icon as={FaUser} h={"14px"} w={"14px"} me={2} color="ink.400" />
          <Text fontSize="xs" color="ink.400" noOfLines={1}>
            {billing.email}
          </Text>
        </Flex>
        {grupo && (
          <Text fontSize="xs" color="ink.400" mt={1} noOfLines={1} title={grupo}>
            {grupo}
          </Text>
        )}
      </Td>

      {/* Columna Apoderado */}
      <Td border="none" minWidth={{ sm: "180px" }} py={2}>
        {apoderadoNombre ? (
          <VStack align="flex-start" spacing={0.5}>
            <Text fontSize="sm" color={textColor} fontWeight="800" lineHeight="1.2" noOfLines={1} title={apoderadoNombre}>
              {apoderadoNombre}
            </Text>
            {apoderadoRut && (
              <Text fontSize="xs" color="ink.400">
                {apoderadoRut}
              </Text>
            )}
            {apoderadoEmail && (
              <Text fontSize="xs" color="sky.ink" noOfLines={1} title={apoderadoEmail}>
                {apoderadoEmail}
              </Text>
            )}
            {apoderadoTelefono && (
              <Text fontSize="xs" color="ink.400">
                📞 {apoderadoTelefono}
              </Text>
            )}
          </VStack>
        ) : (
          <Text fontSize="xs" color="ink.400">-</Text>
        )}
      </Td>

      {/* Columna Fecha de Pago */}
      <Td border="none" minWidth={{ sm: "90px" }} py={2}>
        <Box>
          <Text fontSize="xs" color={textColor} fontWeight="600" lineHeight="1.2">
            {formattedDatePaid}
          </Text>
          <Text fontSize="xs" color="ink.400" mt={0.5}>
            {formattedTimePaid}
          </Text>
        </Box>
      </Td>

      {/* Columna Actualización */}
      <Td border="none" minWidth={{ sm: "90px" }} py={2}>
        <Box>
          <Text fontSize="xs" color={textColor} fontWeight="600" lineHeight="1.2">
            {formattedDateModified}
          </Text>
          <Text fontSize="xs" color="ink.400" mt={0.5}>
            {formattedTimeModified}
          </Text>
        </Box>
      </Td>

      {/* Columna Medio de Pago */}
      <Td border="none" minWidth={{ sm: "80px" }} py={2}>
        <Box textAlign="center">
          <Text fontSize="xs" color={paymentMethod === 'Webpay' ? 'sky.ink' : 'mint.ink'} fontWeight="600">
            {paymentMethod === 'Webpay' ? '💳' : '🏪'} {paymentMethod}
          </Text>
        </Box>
      </Td>

      {/* Columna Boleta Combinada - Icono + Número */}
      <Td border="none" minWidth={{ sm: "100px" }} py={2}>
        <VStack align="center" spacing={1}>
          {billmysalesOrderId ? (
            <Tooltip label="Descargar PDF de Factura" hasArrow>
              <IconButton
                icon={loadingPdf ? <Spinner size="sm" /> : <Icon as={FaFilePdf} />}
                bg="cream.bg2"
                color="coral.700"
                _hover={{ bg: 'coral.soft' }}
                variant="ghost"
                size="sm"
                aria-label="Descargar PDF"
                onClick={handleGetBillMySalesPdf}
                isLoading={loadingPdf}
              />
            </Tooltip>
          ) : (
            <Icon as={FaFilePdf} color="cream.line2" />
          )}

          {invoiceNumber ? (
            <Text fontSize="xs" color="sky.ink" fontWeight="700" textAlign="center">
              {invoiceNumber}
            </Text>
          ) : (
            <Text fontSize="xs" color="ink.400" textAlign="center" fontStyle="italic">
              Sin núm.
            </Text>
          )}
        </VStack>
      </Td>
    </Tr>
  );
};

export default PaymentTableRow;